// Musteri secici dialog — server-side datatable, telefon/ad/eposta uzerinden arama.
// Aktivite log filtre alaninda kullanilir. Son kayit en ustte.

import { useState, useMemo } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { useQuery } from '@tanstack/react-query';
import { Search, Smartphone, Users, X } from 'lucide-react';
import { BosDurum, Girdi, Rozet, VeriTablo, type ColumnDef, type RozetTonu } from '@emanetx/ui';
import { musterileriListele, type Musteri } from '@emanetx/sdk';
import { istemci } from '../istemci.js';

export function platformTon(p: 'IOS' | 'ANDROID' | 'WEB'): RozetTonu {
  return p === 'IOS' ? 'mavi' : p === 'ANDROID' ? 'yesil' : 'fume';
}

export function yasHesapla(dogumTarihi: string | null | undefined): number | null {
  if (!dogumTarihi) return null;
  const d = new Date(dogumTarihi);
  if (Number.isNaN(d.getTime())) return null;
  const bugun = new Date();
  let yas = bugun.getFullYear() - d.getFullYear();
  const aylik = bugun.getMonth() - d.getMonth();
  if (aylik < 0 || (aylik === 0 && bugun.getDate() < d.getDate())) yas--;
  return yas;
}

function useDebounced<T>(deger: T, gecikme = 300): T {
  const [d, setD] = useState(deger);
  useMemo(() => {
    const t = setTimeout(() => setD(deger), gecikme);
    return () => clearTimeout(t);
  }, [deger, gecikme]);
  return d;
}

export function MusteriSeciciDialog({
  acik,
  onDegisim,
  onSec,
}: {
  acik: boolean;
  onDegisim: (v: boolean) => void;
  onSec: (m: Musteri) => void;
}) {
  const [sayfa, setSayfa] = useState(1);
  const [aramaInput, setAramaInput] = useState('');
  const arama = useDebounced(aramaInput, 300);

  const sorgu = { sayfa, boyut: 20, ...(arama && { arama }) };
  const liste = useQuery({
    queryKey: ['musteri-secici', sorgu],
    queryFn: () => musterileriListele(istemci, sorgu),
    placeholderData: (o) => o,
    enabled: acik,
  });

  const kolonlar = useMemo<ColumnDef<Musteri, unknown>[]>(
    () => [
      {
        accessorKey: 'adSoyad',
        header: 'Ad Soyad',
        meta: { etiket: 'Ad Soyad' },
        cell: ({ row }) => (
          <span className="text-fume-900 dark:text-fume-50 text-sm font-medium">
            {row.original.adSoyad}
          </span>
        ),
      },
      {
        accessorKey: 'telefon',
        header: 'Telefon',
        meta: { etiket: 'Telefon' },
        cell: ({ row }) => (
          <span className="text-fume-600 dark:text-fume-300 font-mono text-xs">
            {row.original.telefon}
          </span>
        ),
      },
      {
        accessorKey: 'dilTercihi',
        header: 'Dil',
        meta: { etiket: 'Dil' },
        cell: ({ row }) => (
          <Rozet ton="fume" className="font-mono text-[10px]">
            {row.original.dilTercihi.toUpperCase()}
          </Rozet>
        ),
      },
      {
        accessorKey: 'dogumTarihi',
        header: 'Doğum / Yaş',
        meta: { etiket: 'Doğum / Yaş' },
        cell: ({ row }) => {
          const dt = row.original.dogumTarihi;
          const yas = yasHesapla(dt);
          if (!dt) return <span className="text-fume-400 text-xs">—</span>;
          return (
            <span className="text-fume-600 dark:text-fume-300 font-mono text-xs">
              {new Date(dt).toLocaleDateString('tr-TR')}
              {yas !== null && (
                <span className="text-marka-600 dark:text-marka-400 ml-2">({yas} yaş)</span>
              )}
            </span>
          );
        },
      },
      {
        id: 'cihaz',
        header: 'Cihaz',
        meta: { etiket: 'Cihaz' },
        cell: ({ row }) => {
          const c = row.original.cihaz;
          if (!c) return <span className="text-fume-400 text-xs">—</span>;
          return (
            <span className="text-fume-600 dark:text-fume-300 inline-flex items-center gap-1.5 text-xs">
              <Smartphone size={11} className="text-fume-400" />
              <Rozet ton={platformTon(c.platform)} className="text-[10px]">
                {c.platform}
              </Rozet>
              {c.cihazAdi && <span className="max-w-[120px] truncate">{c.cihazAdi}</span>}
              {c.uygulamaSurum && (
                <span className="text-fume-400 font-mono">v{c.uygulamaSurum}</span>
              )}
            </span>
          );
        },
      },
      {
        accessorKey: 'kayitTarihi',
        header: 'Kayıt',
        meta: { etiket: 'Kayıt' },
        cell: ({ row }) => (
          <span className="text-fume-500 text-xs">
            {new Date(row.original.kayitTarihi).toLocaleDateString('tr-TR', {
              day: 'numeric',
              month: 'short',
              year: '2-digit',
            })}
          </span>
        ),
      },
    ],
    [],
  );

  return (
    <Dialog.Root open={acik} onOpenChange={onDegisim}>
      <Dialog.Portal>
        <Dialog.Overlay className="bg-fume-900/50 fixed inset-0 z-40 backdrop-blur-sm" />
        <Dialog.Content className="dark:bg-fume-900 fixed left-1/2 top-1/2 z-50 flex max-h-[90vh] w-[96vw] max-w-5xl -translate-x-1/2 -translate-y-1/2 flex-col rounded-2xl bg-white shadow-2xl">
          <div className="border-fume-100 dark:border-fume-800 flex items-start justify-between gap-3 border-b p-5">
            <div className="min-w-0 flex-1">
              <Dialog.Title className="text-fume-900 dark:text-fume-50 flex items-center gap-2 text-base font-semibold">
                <Users size={16} className="text-marka-500" /> Müşteri Seç
              </Dialog.Title>
              <Dialog.Description className="text-fume-500 dark:text-fume-400 mt-1 text-sm">
                Telefon, ad veya e-posta üzerinden arayın. Satıra tıklayarak seçin.
              </Dialog.Description>
            </div>
            <Dialog.Close asChild>
              <button
                aria-label="Kapat"
                className="text-fume-400 hover:text-fume-700 dark:hover:text-fume-100"
              >
                <X size={18} />
              </button>
            </Dialog.Close>
          </div>

          <div className="flex-1 overflow-auto p-5">
            <VeriTablo
              veri={liste.data?.veriler ?? []}
              kolonlar={kolonlar}
              toplam={liste.data?.toplam}
              sayfa={sayfa}
              boyut={20}
              onSayfa={setSayfa}
              yukleniyor={liste.isLoading}
              yenileniyor={liste.isFetching}
              onYenile={() => void liste.refetch()}
              onSatirTikla={(m) => {
                onSec(m);
                onDegisim(false);
              }}
              bosDurum={
                <BosDurum
                  ikon={Users}
                  baslik={arama ? 'Eşleşen müşteri yok' : 'Müşteri bulunamadı'}
                  aciklama={arama ? 'Aramayı değiştirin.' : 'Henüz kayıtlı müşteri yok.'}
                />
              }
              ustAksiyonlar={
                <div className="relative">
                  <Search
                    size={13}
                    className="text-fume-400 pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2"
                  />
                  <Girdi
                    placeholder="Telefon / ad / e-posta ara"
                    value={aramaInput}
                    onChange={(e) => {
                      setAramaInput(e.target.value);
                      setSayfa(1);
                    }}
                    className="h-10 w-72 pl-8 text-sm"
                    autoFocus
                  />
                </div>
              }
            />
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
